import { FC, FormEvent, useEffect, useRef, useState } from 'react';

import { CloseIcon, TTranslator } from '@smartshell/ui-kit';
import QRCodeStyling from 'qr-code-styling';

import { TLocalesKeys } from 'localization';

import s from './ShellAuthSidebar.module.scss';

const SMART_GAMER_URL = 'https://smartgamer.ru';
const SHELL_LOGO_URL = '/shell/auth-logo.svg';

interface IShellAuthSidebarProps {
  hostAlias: string;
  hostGroupTitle: string;
  isLoading?: boolean;
  t: TTranslator<TLocalesKeys>;
  onClose: () => void;
  onLogin: () => Promise<void> | void;
}

export const ShellAuthSidebar: FC<IShellAuthSidebarProps> = ({
  hostAlias,
  hostGroupTitle,
  isLoading = false,
  t,
  onClose,
  onLogin,
}) => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = qrRef.current;

    if (!container) return;

    const qrCode = new QRCodeStyling({
      width: 178,
      height: 178,
      type: 'svg',
      data: SMART_GAMER_URL,
      qrOptions: { errorCorrectionLevel: 'H' },
      dotsOptions: { color: '#000000', type: 'square', roundSize: true },
      cornersSquareOptions: { color: '#000000', type: 'square' },
      cornersDotOptions: { color: '#000000', type: 'square' },
      backgroundOptions: { color: '#FFFFFF' },
    });

    container.replaceChildren();
    qrCode.append(container);

    return () => container.replaceChildren();
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    await onLogin();
  };

  return (
    <section
      className={s.ssShellAuthSidebar}
      aria-label={t('shell.auth.title')}
    >
      <button
        className={s.ssShellAuthClose}
        type="button"
        aria-label={t('shell.auth.close')}
        onClick={onClose}
      >
        <CloseIcon color="light" size="m" />
      </button>
      <header className={s.ssShellAuthHeader}>
        <img
          className={s.ssShellAuthLogo}
          src={SHELL_LOGO_URL}
          alt="SmartShell"
        />
        <div className={s.ssShellAuthHost}>
          <h2>{hostAlias}</h2>
          <p>{hostGroupTitle}</p>
        </div>
      </header>
      <div className={s.ssShellAuthContent}>
        <form className={s.ssShellAuthForm} onSubmit={submit}>
          <div className={s.ssShellAuthTitle}>
            <h1>{t('shell.auth.title')}</h1>
            <p>{t('shell.auth.hint')}</p>
          </div>
          <div className={s.ssShellAuthFields}>
            <label className={s.ssShellAuthField}>
              <span>{t('phone')}</span>
              <input
                value={login}
                type="tel"
                autoComplete="username"
                onChange={(event) => setLogin(event.target.value)}
                data-testid="ss-shell-auth-login"
              />
            </label>
            <label className={s.ssShellAuthField}>
              <span>{t('password')}</span>
              <input
                value={password}
                type="password"
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
                data-testid="ss-shell-auth-password"
              />
            </label>
            <ShellAuthHintLink
              label={t('shell.auth.forgot')}
              hint={t('shell.auth.forgot.hint')}
            />
          </div>
          <div className={s.ssShellAuthActions}>
            <button
              className={s.ssShellAuthLogin}
              type="submit"
              disabled={isLoading}
              data-testid="ss-shell-auth-submit"
            >
              {t('shell.guest.login')}
            </button>
            <ShellAuthHintLink
              label={t('shell.auth.register')}
              hint={t('shell.auth.register.hint')}
              centered
            />
          </div>
        </form>
        <a
          className={s.ssShellAuthQr}
          href={SMART_GAMER_URL}
          target="_blank"
          rel="noreferrer"
          aria-label={t('shell.auth.qr.link')}
        >
          <div className={s.ssShellAuthQrPhone}>
            <div className={s.ssShellAuthQrFrame} ref={qrRef} />
            <span className={s.ssShellAuthQrLogo} aria-hidden>
              <img src={SHELL_LOGO_URL} alt="" />
            </span>
          </div>
          <p>
            {t('shell.auth.qr.title')}
            <br />
            {t('shell.auth.qr.description')}{' '}
            <strong>{t('shell.auth.qr.application')}</strong>
          </p>
        </a>
      </div>
    </section>
  );
};

interface IShellAuthHintLinkProps {
  label: string;
  hint: string;
  centered?: boolean;
}

const ShellAuthHintLink: FC<IShellAuthHintLinkProps> = ({
  label,
  hint,
  centered = false,
}) => (
  <button
    className={`${s.ssShellAuthHintLink} ${
      centered ? s.ssShellAuthHintLink_centered : ''
    }`}
    type="button"
    onClick={() => undefined}
  >
    {label}
    <span className={s.ssShellAuthTooltip} role="tooltip">
      {hint}
    </span>
  </button>
);
